import { useCallback, useState } from "react";

/**
 * "Continue with Apple" (https://developer.apple.com/documentation/signinwithapplejs).
 *
 * The iPhone app signs in with Apple outside India, so somebody who signed up
 * that way needs Apple here too to reach the same account. Apple's identity
 * token goes to /users/apple_login, which checks it was issued to one of
 * Fleek's own client ids (APPLE_SIGNIN_CLIENT_IDS). On the web that client id
 * is a Services ID, not the app's bundle id, so REACT_APP_APPLE_SERVICES_ID
 * must be added to that list, and the Services ID must have web.getfleek.app
 * as a domain and REACT_APP_APPLE_REDIRECT_URI as a return URL.
 *
 * With REACT_APP_APPLE_SERVICES_ID unset this renders nothing.
 */

const SCRIPT_SRC =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

export const APPLE_SERVICES_ID = process.env.REACT_APP_APPLE_SERVICES_ID || "";
const REDIRECT_URI =
  process.env.REACT_APP_APPLE_REDIRECT_URI ||
  (typeof window !== "undefined" ? `${window.location.origin}/` : "");

let scriptPromise = null;

function loadScript() {
  if (typeof window === "undefined" || window.AppleID?.auth) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = SCRIPT_SRC;
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => {
        scriptPromise = null; // allow a retry on the next tap
        reject(new Error("Apple sign-in script failed to load"));
      };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

// Apple sends the name only on the very first authorisation.
const nameFrom = (user) =>
  [user?.name?.firstName, user?.name?.lastName].filter(Boolean).join(" ").trim();

/**
 * onCredential(identityToken, name) once Apple answers; onError() if Apple
 * can't be reached. Closing Apple's popup is not an error.
 */
export const AppleSignIn = ({ onCredential, onError, className }) => {
  const [loading, setLoading] = useState(false);

  const signIn = useCallback(async () => {
    setLoading(true);
    try {
      await loadScript();
      window.AppleID.auth.init({
        clientId: APPLE_SERVICES_ID,
        scope: "name email",
        redirectURI: REDIRECT_URI,
        usePopup: true,
      });
      const res = await window.AppleID.auth.signIn();
      const token = res?.authorization?.id_token;
      if (token) onCredential?.(token, nameFrom(res?.user));
    } catch (err) {
      if (err?.error !== "popup_closed_by_user" && err?.error !== "user_cancelled_authorize") {
        onError?.();
      }
    } finally {
      setLoading(false);
    }
  }, [onCredential, onError]);

  if (!APPLE_SERVICES_ID) return null;
  return (
    <button type="button" className={className} onClick={signIn} disabled={loading}>
      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 814 1000" fill="currentColor">
        <path d="M788 341c-6 4-108 62-108 190 0 148 130 200 134 202-1 3-21 72-69 142-43 62-88 124-156 124s-86-40-165-40c-77 0-104 41-166 41s-106-58-156-128C44 790 0 663 0 543c0-193 125-295 249-295 66 0 120 43 161 43 39 0 101-46 176-46 29 0 131 3 202 96zM554 159c31-37 53-88 53-139 0-7-1-14-2-20-50 2-110 34-146 76-28 32-55 83-55 135 0 8 1 16 2 18 3 1 9 2 14 2 45 0 101-30 134-72z" />
      </svg>
      {loading ? "Opening Apple…" : "Continue with Apple"}
    </button>
  );
};

export default AppleSignIn;
